import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/api';
import { cardsService } from '../../services/cardsService';
import { tableService } from '../../services/tableService';
import { userService } from '../../services/userService';
import { getApiUserCode } from '../Shared/ApiUserBadge';
import DeletePositionWidget from './DeletePositionWidget';
import DashboardEventsWidget from './DashboardEventsWidget';
import MovePositionWidget from './MovePositionWidget';
import CorrectEventDateWidget from './CorrectEventDateWidget';
import './UserDashboard.css';

const UserDashboard = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tablesError, setTablesError] = useState('');
  const [vendor, setVendor] = useState('dice');
  const [userQuery, setUserQuery] = useState('');
  const [selectedVendorUser, setSelectedVendorUser] = useState(null);
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [loginInfo, setLoginInfo] = useState('');
  const [vendorLoading, setVendorLoading] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [vendorMessage, setVendorMessage] = useState('');
  const [vendorError, setVendorError] = useState('');
  const [vendorCardQuery, setVendorCardQuery] = useState('');
  const [selectedVendorCard, setSelectedVendorCard] = useState(null);
  const [vendorCardSuggestions, setVendorCardSuggestions] = useState([]);
  const [vendorCardSuggestionsOpen, setVendorCardSuggestionsOpen] = useState(false);
  const [vendorCardSuggestionsLoading, setVendorCardSuggestionsLoading] = useState(false);
  const [lookupVendor, setLookupVendor] = useState('dice');
  const [lookupUserQuery, setLookupUserQuery] = useState('');
  const [selectedLookupUser, setSelectedLookupUser] = useState(null);
  const [lookupSuggestions, setLookupSuggestions] = useState([]);
  const [lookupSuggestionsOpen, setLookupSuggestionsOpen] = useState(false);
  const [lookupSuggestionsLoading, setLookupSuggestionsLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [emptyVendor, setEmptyVendor] = useState('dice');
  const [emptyLocation, setEmptyLocation] = useState('New York');
  const [emptyLoading, setEmptyLoading] = useState(false);
  const [emptyError, setEmptyError] = useState('');
  const [emptyAccounts, setEmptyAccounts] = useState(null);
  const navigate = useNavigate();
  const vendorOptions = [
    { value: 'dice', label: 'Dice' },
    { value: 'aspen', label: 'Belly Up' },
    { value: 'pacha', label: 'Pacha' },
    { value: 'ticketmaster', label: 'Ticketmaster' },
    { value: 'get_in', label: 'Get-In' },
    { value: 'seetickets', label: 'Seetickets' },
    { value: 'axs', label: 'AXS' },
    { value: 'tao', label: 'Tao' },
    { value: 'tixr', label: 'Tixr' },
  ];
  const inventoryVendorOptions = [
    { value: 'dice', label: 'Dice' },
    { value: 'aspen', label: 'Belly Up' },
    { value: 'tao', label: 'Tao' },
    { value: 'get_in', label: 'Get-In' },
    { value: 'tixr', label: 'Tixr' },
    { value: 'ticketmaster', label: 'Ticketmaster' },
  ];
  const emptyAccountVendorOptions = [
    { value: 'dice', label: 'Dice' },
    { value: 'aspen', label: 'Belly Up' },
    { value: 'tao', label: 'Tao' },
    { value: 'get_in', label: 'Get-In' },
    { value: 'seetickets', label: 'Seetickets' },
    { value: 'axs', label: 'AXS' },
  ];
  const emptyAccountLocationOptions = {
    dice: ['New York', 'Miami', 'Los Angeles', 'Universal', 'Europe', 'London'],
    aspen: ['Aspen'],
    tao: ['New York', 'Las Vegas', 'Chicago', 'Universal'],
    seetickets: ['New York', 'Texas', 'Hawai', 'Universal'],
    default: ['Universal'],
  };
  const currentEmptyLocationOptions =
    emptyAccountLocationOptions[emptyVendor] || emptyAccountLocationOptions.default;
  const currentApiUserCode = currentUser ? getApiUserCode(currentUser.email) : '';
  const canSeeFinancials = ['TS', 'DV'].includes(currentApiUserCode);
  const userGuideUrl = `${process.env.PUBLIC_URL || ''}/Tikwiz_User_Guide.pdf`;
  const selectedVendorLabel = vendorOptions.find((option) => option.value === vendor)?.label || 'Vendor';
  const vendorUsesEmailLogin = vendor !== 'dice';

  useEffect(() => {
    const loadCurrentUser = async () => {
      const token = authService.getToken();
      if (!token) {
        navigate('/');
        return;
      }

      try {
        const user = await authService.getCurrentUser(token);
        setCurrentUser(user);
      } catch (error) {
        console.error('Failed to load current user:', error);
        authService.logout();
        navigate('/');
      }
    };

    loadCurrentUser();
  }, [navigate]);

  useEffect(() => {
    const loadTables = async () => {
      setTablesLoading(true);
      const result = await tableService.listAccessibleTables();
      if (result.success) {
        setTables(result.tables);
        setTablesError('');
      } else {
        setTablesError(result.error);
      }
      setTablesLoading(false);
    };

    loadTables();
  }, []);

  useEffect(() => {
    const query = userQuery.trim();
    if (!query) {
      setUserSuggestions([]);
      setSuggestionsOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setSuggestionsLoading(true);
      const result = await userService.searchVendorUsers(query);
      if (result.success) {
        setUserSuggestions(result.items || []);
        setSuggestionsOpen(true);
      } else {
        setUserSuggestions([]);
        setSuggestionsOpen(false);
      }
      setSuggestionsLoading(false);
    }, 250);

    return () => clearTimeout(timer);
  }, [userQuery]);

  useEffect(() => {
    const query = vendorCardQuery.trim();
    if (!query || selectedVendorCard?.card_name === query) {
      setVendorCardSuggestions([]);
      setVendorCardSuggestionsOpen(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setVendorCardSuggestionsLoading(true);
      const result = await cardsService.listCards({ q: query, limit: 8 });
      setVendorCardSuggestionsLoading(false);
      if (result.success) {
        setVendorCardSuggestions(result.items || []);
        setVendorCardSuggestionsOpen(true);
      } else {
        setVendorCardSuggestions([]);
        setVendorCardSuggestionsOpen(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [selectedVendorCard, vendorCardQuery]);

  useEffect(() => {
    const query = lookupUserQuery.trim();
    if (!query) {
      setLookupSuggestions([]);
      setLookupSuggestionsOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLookupSuggestionsLoading(true);
      const result = await userService.searchVendorUsers(query);
      if (result.success) {
        setLookupSuggestions(result.items || []);
        setLookupSuggestionsOpen(true);
      } else {
        setLookupSuggestions([]);
        setLookupSuggestionsOpen(false);
      }
      setLookupSuggestionsLoading(false);
    }, 250);

    return () => clearTimeout(timer);
  }, [lookupUserQuery]);

  useEffect(() => {
    if (vendorUsesEmailLogin && selectedVendorUser?.email && !loginInfo.trim()) {
      setLoginInfo(selectedVendorUser.email);
    }
  }, [loginInfo, selectedVendorUser, vendorUsesEmailLogin]);

  const handleLogout = () => {
    authService.logout();
    navigate('/');
  };

  const selectVendorUser = (item) => {
    setSelectedVendorUser(item);
    setUserQuery(item.username);
    setSuggestionsOpen(false);
    if (vendorUsesEmailLogin) {
      setLoginInfo(item.email || '');
    }
  };

  const selectVendorCard = (card) => {
    setSelectedVendorCard(card);
    setVendorCardQuery(card.card_name || '');
    setVendorCardSuggestions([]);
    setVendorCardSuggestionsOpen(false);
  };

  const handleVendorSubmit = async (event) => {
    event.preventDefault();
    setVendorMessage('');
    setVendorError('');

    if (!selectedVendorUser?.user_id) {
      setVendorError('Please select a User from autocomplete.');
      return;
    }

    if (!loginInfo.trim()) {
      setVendorError('Login info is required.');
      return;
    }

    if (loginInfo.trim().length > 50) {
      setVendorError('Login info must be 50 characters or fewer.');
      return;
    }

    setVendorLoading(true);
    const result = await userService.saveVendorLoginInfo(
      vendor,
      loginInfo.trim(),
      selectedVendorUser.user_id,
      {
        location: null,
        cardId: selectedVendorCard?.card_id || null,
      }
    );
    if (result.success) {
      setVendorMessage(result.data?.message || 'Vendor info saved.');
      setLoginInfo('');
      setUserQuery('');
      setVendorCardQuery('');
      setSelectedVendorCard(null);
      setVendorCardSuggestions([]);
      setVendorCardSuggestionsOpen(false);
      setSelectedVendorUser(null);
      setUserSuggestions([]);
      setSuggestionsOpen(false);
    } else {
      setVendorError(result.error);
    }
    setVendorLoading(false);
  };

  const handleInventoryLookup = async (event) => {
    event.preventDefault();
    setLookupError('');
    setLookupResult(null);

    if (!selectedLookupUser?.user_id) {
      setLookupError('Please select a User from autocomplete.');
      return;
    }

    setLookupLoading(true);
    const result = await userService.lookupInventory(
      selectedLookupUser.user_id,
      lookupVendor
    );

    if (result.success) {
      setLookupResult(result.data);
    } else {
      setLookupError(result.error);
    }
    setLookupLoading(false);
  };

  const handleEmptyAccountsLookup = async (event) => {
    event.preventDefault();
    setEmptyError('');
    setEmptyAccounts(null);

    if (!emptyVendor || !emptyLocation) {
      setEmptyError('Vendor and location are required.');
      return;
    }

    setEmptyLoading(true);
    const result = await userService.lookupEmptyAccounts(emptyVendor, emptyLocation);
    if (result.success) {
      setEmptyAccounts({
        items: result.items || [],
        totalCount: result.totalCount || 0,
      });
    } else {
      setEmptyError(result.error);
    }
    setEmptyLoading(false);
  };

  const handleEmptyVendorChange = (value) => {
    const nextOptions = emptyAccountLocationOptions[value] || emptyAccountLocationOptions.default;
    setEmptyVendor(value);
    setEmptyLocation(nextOptions[0]);
    setEmptyAccounts(null);
    setEmptyError('');
  };

  if (!currentUser) {
    return (
      <div className="user-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="user-dashboard">
      <header className="user-dashboard-header">
        <div className="header-content">
          <h1>Tikwiz Inventory Manager</h1>
          <div className="user-info">
            <span>Signed in as {currentUser.email}</span>
            <a
              className="user-guide-link"
              href={userGuideUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open user guide"
              title="Open user guide"
            >
              i
            </a>
            <button className="btn btn-secondary" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="user-dashboard-main">
        <section className="quick-actions-section">
          <div className="section-header">
            <h2>Quick Actions</h2>
          </div>
          <div className="quick-action-buttons">
            <Link className="btn btn-primary" to="/user/events">
              Events
            </Link>
            <Link className="btn btn-primary" to="/user/accounts">
              Accounts
            </Link>
            <Link className="btn btn-primary" to="/user/transactions">
              Transactions
            </Link>
            <Link
              className="btn btn-primary"
              to="/user/manual-entry?origin=dashboard&returnTo=/user"
            >
              Manual Entry
            </Link>
            <Link className="btn btn-primary" to="/user/info/artists">
              Info
            </Link>
            <button className="btn btn-primary" type="button" disabled>
              Analytics
            </button>
            {canSeeFinancials && (
              <Link className="btn btn-primary" to="/user/financials">
                Financials
              </Link>
            )}
          </div>
        </section>

        <DashboardEventsWidget showOverview={canSeeFinancials} />

        <section className="rtu-lookup-section">
          <div className="section-header">
            <h2>RTU Lookup</h2>
          </div>
          <form className="empty-accounts-form" onSubmit={handleEmptyAccountsLookup}>
            <div className="form-group">
              <label htmlFor="empty-vendor">Vendor</label>
              <select
                id="empty-vendor"
                value={emptyVendor}
                onChange={(event) => handleEmptyVendorChange(event.target.value)}
                disabled={emptyLoading}
              >
                {emptyAccountVendorOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="empty-location">Location</label>
              <select
                id="empty-location"
                value={emptyLocation}
                onChange={(event) => setEmptyLocation(event.target.value)}
                disabled={emptyLoading}
              >
                {currentEmptyLocationOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <button className="btn btn-primary" type="submit" disabled={emptyLoading}>
              {emptyLoading ? 'Searching...' : 'Lookup RTU'}
            </button>
          </form>
          {emptyError && <div className="notification error empty-accounts-message">{emptyError}</div>}
          {emptyAccounts && (
            <div className="empty-accounts-result">
              <div className="empty-accounts-result-header">
                <strong>{emptyAccounts.totalCount} accounts</strong>
                <span>{emptyLocation}</span>
              </div>
              {emptyAccounts.items.length === 0 ? (
                <p>No empty accounts found.</p>
              ) : (
                <div className="empty-accounts-list">
                  {emptyAccounts.items.map((item) => (
                    <div key={item.user_id} className="empty-account-row">
                      <div>
                        <strong>{item.username}</strong>
                        <span>{item.email || item.name || ''}</span>
                      </div>
                      <code>{item.login_info || '-'}</code>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        <section className="inventory-lookup-section">
          <div className="section-header">
            <h2>Inventory Lookup</h2>
            <span className="section-subtitle">Lookup inventory by user and vendor</span>
          </div>
          {lookupError && <div className="notification error">{lookupError}</div>}
          <form className="inventory-lookup-form" onSubmit={handleInventoryLookup}>
            <div className="form-group user-autocomplete-group">
              <label htmlFor="inventory-user">User</label>
              <input
                id="inventory-user"
                type="text"
                value={lookupUserQuery}
                onChange={(event) => {
                  setLookupUserQuery(event.target.value);
                  setSelectedLookupUser(null);
                }}
                placeholder="Enter Username/Email"
                disabled={lookupLoading}
                autoComplete="off"
              />
              {lookupSuggestionsOpen && (
                <div className="autocomplete-list">
                  {lookupSuggestionsLoading && (
                    <button type="button" className="autocomplete-item" disabled>
                      Searching...
                    </button>
                  )}
                  {!lookupSuggestionsLoading && lookupSuggestions.length === 0 && (
                    <button type="button" className="autocomplete-item" disabled>
                      No users found
                    </button>
                  )}
                  {!lookupSuggestionsLoading &&
                    lookupSuggestions.map((item) => (
                      <button
                        type="button"
                        key={item.user_id}
                        className="autocomplete-item"
                        onClick={() => {
                          setSelectedLookupUser(item);
                          setLookupUserQuery(item.username);
                          setLookupSuggestionsOpen(false);
                        }}
                      >
                        <strong>{item.username}</strong>
                        <span>{item.email || item.name || ''}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="inventory-vendor">Vendor</label>
              <select
                id="inventory-vendor"
                value={lookupVendor}
                onChange={(event) => setLookupVendor(event.target.value)}
                disabled={lookupLoading}
              >
                {inventoryVendorOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <button className="btn btn-primary" type="submit" disabled={lookupLoading}>
              {lookupLoading ? 'Looking up...' : 'Lookup User'}
            </button>
          </form>
          {lookupResult && (
            <div className="inventory-lookup-result">
              <h3>Result</h3>
              <p><strong>Amount:</strong> {Number(lookupResult.inventory_amount || 0).toFixed(2)}</p>
              <p><strong>Login Info:</strong> {lookupResult.login_info || '-'}</p>
              <p><strong>Tickets:</strong> {lookupResult.tickets ?? 0}</p>
              <p><strong>Shows:</strong> {lookupResult.shows ?? 0}</p>
              <p>
                <strong>Event Names:</strong>{' '}
                {(lookupResult.event_names || []).length
                  ? lookupResult.event_names.join(', ')
                  : '-'}
              </p>
              <p><strong>Location:</strong> {lookupResult.location || '-'}</p>
              <p><strong>Card:</strong> {lookupResult.card || '-'}</p>
            </div>
          )}
        </section>

        <section className="vendor-login-section">
          <div className="section-header">
            <h2>Vendor Login Info</h2>
            <span className="section-subtitle">Save account login details</span>
          </div>
          {vendorError && <div className="notification error">{vendorError}</div>}
          {vendorMessage && <div className="notification success">{vendorMessage}</div>}
          <form className="vendor-login-form" onSubmit={handleVendorSubmit}>
            <div className="form-group user-autocomplete-group">
              <label htmlFor="vendor-user">User</label>
              <input
                id="vendor-user"
                type="text"
                value={userQuery}
                onChange={(event) => {
                  setUserQuery(event.target.value);
                  setSelectedVendorUser(null);
                  if (vendorUsesEmailLogin) {
                    setLoginInfo('');
                  }
                }}
                placeholder="Enter Username/Email"
                disabled={vendorLoading}
                autoComplete="off"
              />
              {suggestionsOpen && (
                <div className="autocomplete-list">
                  {suggestionsLoading && (
                    <button type="button" className="autocomplete-item" disabled>
                      Searching...
                    </button>
                  )}
                  {!suggestionsLoading && userSuggestions.length === 0 && (
                    <button type="button" className="autocomplete-item" disabled>
                      No users found
                    </button>
                  )}
                  {!suggestionsLoading &&
                    userSuggestions.map((item) => (
                      <button
                        type="button"
                        key={item.user_id}
                        className="autocomplete-item"
                        onClick={() => selectVendorUser(item)}
                      >
                        <strong>{item.username}</strong>
                        <span>{item.email || item.name || ''}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="vendor-select">Vendor</label>
              <select
                id="vendor-select"
                value={vendor}
                onChange={(event) => {
                  const nextVendor = event.target.value;
                  setVendor(nextVendor);
                  setVendorCardQuery('');
                  setSelectedVendorCard(null);
                  setVendorCardSuggestions([]);
                  setVendorCardSuggestionsOpen(false);
                  if (nextVendor !== 'dice') {
                    setLoginInfo(selectedVendorUser?.email || '');
                  }
                }}
                disabled={vendorLoading}
              >
                {vendorOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="login-info">
                {vendor === 'dice' ? 'Dice Login Info (Phone)' : `${selectedVendorLabel} Login Email`}
              </label>
              <input
                id="login-info"
                type="text"
                maxLength={50}
                value={loginInfo}
                onChange={(event) => setLoginInfo(event.target.value)}
                placeholder={vendor === 'dice' ? 'Enter Dice Phone' : `Enter ${selectedVendorLabel} Email`}
                disabled={vendorLoading}
              />
            </div>
            <div className="form-group user-autocomplete-group">
              <label htmlFor="vendor-card">{vendor === 'dice' ? 'Saved Card' : 'Card'}</label>
              <input
                id="vendor-card"
                type="text"
                value={vendorCardQuery}
                onChange={(event) => {
                  setVendorCardQuery(event.target.value);
                  setSelectedVendorCard((current) => (
                    current && current.card_name === event.target.value ? current : null
                  ));
                }}
                placeholder="Optional card search"
                disabled={vendorLoading}
                autoComplete="off"
              />
              {vendorCardSuggestionsOpen && (
                <div className="autocomplete-list">
                  {vendorCardSuggestionsLoading && (
                    <button type="button" className="autocomplete-item" disabled>
                      Searching...
                    </button>
                  )}
                  {!vendorCardSuggestionsLoading && vendorCardSuggestions.length === 0 && (
                    <button type="button" className="autocomplete-item" disabled>
                      No cards found
                    </button>
                  )}
                  {!vendorCardSuggestionsLoading &&
                    vendorCardSuggestions.map((item) => (
                      <button
                        type="button"
                        key={item.card_id}
                        className="autocomplete-item"
                        onClick={() => selectVendorCard(item)}
                      >
                        <strong>{item.card_name}</strong>
                        <span>{item.owner_username || item.owner_email || ''}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
            <button className="btn btn-primary" type="submit" disabled={vendorLoading}>
              {vendorLoading ? 'Saving...' : 'Save Login Info'}
            </button>
          </form>
        </section>

        <MovePositionWidget className="user-dashboard-move-widget" />
        <DeletePositionWidget className="user-dashboard-move-widget" />

        <section className="user-table-preview">
          <div className="section-header">
            <h2>My Tables</h2>
            <span className="section-subtitle">{tables.length} available</span>
          </div>
          {tablesError && <div className="notification error">{tablesError}</div>}
          {tablesLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading tables...</p>
            </div>
          ) : (
            <div className="user-tables-grid">
              {tables.map((table) => (
                <button
                  key={table.id}
                  className="user-table-card"
                  onClick={() => navigate(`/user/tables/${table.table_name}`)}
                >
                  <div>
                    <h3>{table.table_name}</h3>
                    <p>{table.description || 'No description yet'}</p>
                  </div>
                  <span className="table-pill">{table.column_count} columns</span>
                </button>
              ))}
              {tables.length === 0 && (
                <div className="empty-state">
                  <p>
                    No tables assigned yet. Ask an admin to grant table permissions.
                  </p>
                </div>
              )}
            </div>
          )}
        </section>

        <CorrectEventDateWidget className="user-dashboard-move-widget" />
      </main>
    </div>
  );
};

export default UserDashboard;
